import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Schedule,
  GroupAdd,
  Visibility,
  Payment,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import SubscriptionWizard from '../../components/Finance/SubscriptionWizard';
import { formatCurrency } from '../../components/Finance/formatCurrency';
import StudentReadOnlyDialog from '../../components/StudentReadOnlyDialog';
import StudentTimePreferencesDialog from '../../components/StudentTimePreferencesDialog';
import BatchAssignmentDialog from '../../components/BatchAssignmentDialog';
import AdmissionStudentEditDialog from '../../components/Admission/AdmissionStudentEditDialog';
import studentsService from '../../services/students';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const PLAN_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  six_months: '6 Months',
  yearly: 'Yearly',
};
const BATCH_LABELS = { one_on_one: '1:1', group: 'Group' };

export default function AdmissionStudents() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('true');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStudent, setWizardStudent] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [viewStudentId, setViewStudentId] = useState(null);
  const [editStudentId, setEditStudentId] = useState(null);
  const [timePrefStudent, setTimePrefStudent] = useState(null);
  const [batchStudent, setBatchStudent] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const status = tab === 0 ? 'needs_billing' : tab === 1 ? 'active' : 'all';

  const { data, isLoading, refetch } = useQuery(
    ['admissionStudents', status, isActiveFilter, search, page, limit],
    async () => {
      const res = await financeAPI.listAdmissionStudents({
        status,
        is_active: isActiveFilter,
        search: search || undefined,
        page: page + 1,
        limit,
      });
      return res.data;
    },
    { keepPreviousData: true }
  );

  const rows = data?.data || [];
  const total = data?.pagination?.total || 0;

  const refresh = () => {
    queryClient.invalidateQueries('admissionStudents');
    refetch();
  };

  const openWizard = (row = null) => {
    if (row) {
      setWizardStudent({
        id: row.student_id,
        firstName: row.first_name,
        lastName: row.last_name,
        email: row.email,
        displayId: row.display_id,
        country: row.country,
      });
    } else {
      setWizardStudent(null);
    }
    setWizardOpen(true);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const rowAsStudent = (row) =>
    row
      ? {
          id: row.student_id,
          display_id: row.display_id,
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          phone: row.phone,
          country: row.country,
          age_group: row.age_group,
          timezone: row.timezone,
        }
      : null;

  const openTimePreferences = async (row) => {
    closeMenu();
    try {
      const res = await studentsService.getStudent(row.student_id);
      const full = res.data || res;
      setTimePrefStudent(full);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load student'));
    }
  };

  const openBatchAssign = (row) => {
    closeMenu();
    setBatchStudent(rowAsStudent(row));
  };

  const handleSaveTimePreferences = async (timePreferences) => {
    if (!timePrefStudent?.id) return;
    try {
      await studentsService.updateTimePreferences(timePrefStudent.id, timePreferences);
      refresh();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            My students
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your student hub — profile, time preferences, billing, and batch assignment in one place.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => openWizard(null)}>
          New subscription
        </Button>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPage(0);
          }}
        >
          <Tab label="Needs billing" />
          <Tab label="Active billing" />
          <Tab label="All" />
        </Tabs>
      </Paper>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name, email, or ID"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="admission-student-status-label">Student status</InputLabel>
          <Select
            labelId="admission-student-status-label"
            label="Student status"
            value={isActiveFilter}
            onChange={(e) => {
              setIsActiveFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Closer</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Next due</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={9}>Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && !rows.length && (
              <TableRow>
                <TableCell colSpan={9}>No students in this list.</TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
              const hasSub = !!row.subscription_id;
              const prefsOk = (row.time_preference_count || 0) > 0;
              return (
                <TableRow key={row.student_id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {name || '—'}
                      </Typography>
                      {row.is_active === false ? (
                        <Chip size="small" label="Inactive" />
                      ) : null}
                    </Stack>
                    {row.age_group ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.age_group}
                        {!prefsOk ? ' · no time prefs' : ''}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{row.display_id || '—'}</TableCell>
                  <TableCell>{row.country || '—'}</TableCell>
                  <TableCell>{row.closer_name || '—'}</TableCell>
                  <TableCell>
                    {row.batch_names || (
                      <Typography variant="caption" color="text.secondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {hasSub ? (
                      <Chip size="small" color="success" label={row.subscription_status || 'active'} />
                    ) : (
                      <Chip size="small" color="warning" label="Needs billing" />
                    )}
                  </TableCell>
                  <TableCell>
                    {hasSub
                      ? `${BATCH_LABELS[row.batch_type] || row.batch_type || '—'} · ${
                          PLAN_LABELS[row.subscription_model] || row.subscription_model || '—'
                        } · ${row.classes_per_week || '—'} /wk`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {hasSub && row.open_amount_due != null
                      ? `${row.next_due_date || '—'} · ${formatCurrency(row.open_amount_due, row.currency)}`
                      : row.next_due_date || '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      {hasSub ? (
                        <Button
                          size="small"
                          onClick={() => navigate(`/admission/subscriptions/${row.subscription_id}`)}
                        >
                          Plan
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => openWizard(row)}>
                          Billing
                        </Button>
                      )}
                      <Tooltip title="More actions">
                        <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setViewStudentId(menuRow?.student_id);
            closeMenu();
          }}
        >
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEditStudentId(menuRow?.student_id);
            closeMenu();
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) openTimePreferences(menuRow);
          }}
        >
          <Schedule fontSize="small" sx={{ mr: 1 }} /> Time preferences
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) openBatchAssign(menuRow);
          }}
        >
          <GroupAdd fontSize="small" sx={{ mr: 1 }} /> Assign to batch
        </MenuItem>
        {menuRow?.subscription_id ? (
          <MenuItem
            onClick={() => {
              navigate(`/admission/subscriptions/${menuRow.subscription_id}`);
              closeMenu();
            }}
          >
            <Payment fontSize="small" sx={{ mr: 1 }} /> View subscription
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              openWizard(menuRow);
              closeMenu();
            }}
          >
            <Payment fontSize="small" sx={{ mr: 1 }} /> Set up subscription
          </MenuItem>
        )}
      </Menu>

      <SubscriptionWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialStudent={wizardStudent}
        lockStudent={!!wizardStudent}
        onCreated={(data) => {
          refresh();
          const id = data?.subscription?.id;
          if (id) navigate(`/admission/subscriptions/${id}`);
        }}
      />

      <StudentReadOnlyDialog
        open={!!viewStudentId}
        studentId={viewStudentId}
        onClose={() => setViewStudentId(null)}
      />

      <AdmissionStudentEditDialog
        open={!!editStudentId}
        studentId={editStudentId}
        onClose={() => setEditStudentId(null)}
        onSaved={refresh}
      />

      {timePrefStudent && (
        <StudentTimePreferencesDialog
          open={!!timePrefStudent}
          onClose={() => setTimePrefStudent(null)}
          student={timePrefStudent}
          onSave={handleSaveTimePreferences}
        />
      )}

      <BatchAssignmentDialog
        open={!!batchStudent}
        student={batchStudent}
        onClose={() => setBatchStudent(null)}
        onSuccess={() => {
          setBatchStudent(null);
          refresh();
          toast.success('Student assigned to batch');
        }}
      />
    </Box>
  );
}
